/* eslint-disable max-len */
import { Col, Row } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { type AppointmentModuleSpecsFragment } from '../../../../api/fragments';
import DealershipTranslatedTextAreaField from '../../../../components/fields/DealershipFields/DealershipTranslatedTextArea';
import SingleUploadField from '../../../../components/fields/SingleUploadField';
import CollapsibleWrapper, { Panel } from '../../../../components/wrappers/CollapsibleWrapper';
import { allowedExtensions } from '../../../../utilities/extensions';

type AppointmentModuleEmailFormProps = {
    prefix?: string;
    module: AppointmentModuleSpecsFragment;
};

type AppointmentModuleItemProps = {
    path:
        | 'customer.submitConfirmation'
        | 'customer.endTestDrive'
        | 'customer.bookingAmendment'
        | 'customer.bookingConfirmation'
        | 'salesPerson.submitConfirmation'
        | 'salesPerson.finderReservation';
    prefix?: string;
    module: AppointmentModuleSpecsFragment;
    children?: React.ReactNode;
};

const AppointmentModuleEmailItem = ({ path, prefix, module, children }: AppointmentModuleItemProps) => {
    const { t } = useTranslation('appointmentModuleDetails');

    const getName = useCallback(
        (name: string) => (prefix ? `${prefix}.${path}.${name}` : `${path}.${name}`),
        [path, prefix]
    );
    const getTranslationKey = useCallback((name: string) => `appointmentModuleDetails:fields.emails.${name}`, []);

    return (
        <>
            <Col lg={8} xs={24}>
                <DealershipTranslatedTextAreaField
                    {...t(getTranslationKey('subject'), { returnObjects: true })}
                    autoSize={{ minRows: 2, maxRows: 6 }}
                    companyId={module.company.id}
                    name={getName('subject')}
                    required
                />
            </Col>
            <Col lg={8} xs={24}>
                <DealershipTranslatedTextAreaField
                    {...t(getTranslationKey('introTitle'), { returnObjects: true })}
                    autoSize={{ minRows: 2, maxRows: 6 }}
                    companyId={module.company.id}
                    name={getName('introTitle')}
                    required
                />
            </Col>
            <Col lg={8} xs={24}>
                <DealershipTranslatedTextAreaField
                    {...t(getTranslationKey('contentText'), { returnObjects: true })}
                    autoSize={{ minRows: 2, maxRows: 6 }}
                    companyId={module.company.id}
                    name={getName('contentText')}
                    required
                    withContentRefinement
                />
            </Col>
            {children}
            <Col lg={8} xs={24}>
                <SingleUploadField
                    extensions={allowedExtensions.image}
                    {...t(getTranslationKey('introImage'), { returnObjects: true })}
                    name={getName('introImage')}
                />
            </Col>
        </>
    );
};

const AppointmentModuleEmailForm = ({ prefix, module }: AppointmentModuleEmailFormProps) => {
    const { t } = useTranslation('appointmentModuleDetails');

    const getName = useCallback((name: string) => (prefix ? `${prefix}.${name}` : name), [prefix]);

    return (
        <CollapsibleWrapper
            defaultActiveKey={[
                'emailContentsCustomerSubmitConfirmation',
                'emailContentsCustomerEndTestDrive',
                'emailContentsCustomerBookingAmendment',
                'emailContentsCustomerBookingConfirmation',
                'emailContentsSalesPersonSubmitConfirmation',
                'emailContentsSalesPersonFinderReservation',
            ]}
        >
            <Panel
                key="emailContentsCustomerSubmitConfirmation"
                header={t('appointmentModuleDetails:sections.emails.customer.submitConfirmation')}
            >
                <Row gutter={10}>
                    <AppointmentModuleEmailItem module={module} path="customer.submitConfirmation" prefix={prefix} />
                </Row>
            </Panel>
            <Panel
                key="emailContentsCustomerEndTestDrive"
                header={t('appointmentModuleDetails:sections.emails.customer.endTestDrive')}
            >
                <Row gutter={10}>
                    <AppointmentModuleEmailItem module={module} path="customer.endTestDrive" prefix={prefix} />
                </Row>
            </Panel>
            <Panel
                key="emailContentsCustomerBookingConfirmation"
                header={t('appointmentModuleDetails:sections.emails.customer.bookingConfirmation')}
            >
                <Row gutter={10}>
                    <AppointmentModuleEmailItem module={module} path="customer.bookingConfirmation" prefix={prefix} />
                </Row>
            </Panel>
            <Panel
                key="emailContentsCustomerBookingAmendment"
                header={t('appointmentModuleDetails:sections.emails.customer.bookingAmendment')}
            >
                <Row gutter={10}>
                    <AppointmentModuleEmailItem module={module} path="customer.bookingAmendment" prefix={prefix} />
                </Row>
            </Panel>
            <Panel
                key="emailContentsSalesPersonSubmitConfirmation"
                header={t('appointmentModuleDetails:sections.emails.salesPerson.submitConfirmation')}
            >
                <Row gutter={10}>
                    <AppointmentModuleEmailItem module={module} path="salesPerson.submitConfirmation" prefix={prefix} />
                </Row>
            </Panel>
            <Panel
                key="emailContentsSalesPersonFinderReservation"
                header={t('appointmentModuleDetails:sections.emails.salesPerson.finderReservation')}
            >
                <Row gutter={10}>
                    <AppointmentModuleEmailItem module={module} path="salesPerson.finderReservation" prefix={prefix}>
                        <Col lg={8} xs={24}>
                            <DealershipTranslatedTextAreaField
                                {...t('appointmentModuleDetails:fields.emails.listTestDriveTitle', {
                                    returnObjects: true,
                                })}
                                autoSize={{ minRows: 2, maxRows: 6 }}
                                companyId={module.company.id}
                                name={getName('salesPerson.finderReservation.listTestDriveTitle')}
                            />
                        </Col>
                        <Col lg={8} xs={24}>
                            <DealershipTranslatedTextAreaField
                                {...t('appointmentModuleDetails:fields.emails.listTestDriveItem', {
                                    returnObjects: true,
                                })}
                                autoSize={{ minRows: 2, maxRows: 6 }}
                                companyId={module.company.id}
                                name={getName('salesPerson.finderReservation.listTestDriveItem')}
                            />
                        </Col>
                    </AppointmentModuleEmailItem>
                </Row>
            </Panel>
        </CollapsibleWrapper>
    );
};

export default AppointmentModuleEmailForm;
